import { createPortal } from "react-dom";
import Immutable from "immutable";
import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { DocumentSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { BaseDocumentSearch } from "@/Documents/Components/DocumentSearch/BaseDocumentSearch";
import { DocumentSettingsContainer } from "@/Documents/Components/DocumentSearch/DocumentSettingsContainer";
import { DocumentSearchActionButtonsRegistryView } from "@/Documents/Extensions/DocumentSearchActionButtonsRegistry";
import { DocumentSearchLeftComponentRegistryView } from "@/Documents/Extensions/DocumentSearchLeftComponentRegistry";
import { DocumentsHelper } from "@/Documents/Helpers/DocumentsHelper";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { SendDocumentStrategy } from "@/Documents/Services/Portal/Strategies/SendDocumentStrategy";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";

import "./DocumentSearch.css";
import documentSearchStyles from "./DocumentSearch.module.scss";
import styles from "./DocumentSettingsContainer.module.scss";

class DocumentSearch extends BaseDocumentSearch {
    protected helper = new MyleHelper();
    private isSelectAllDocument: boolean = false;
    private tableCellClass: string = "text-wrap-control";
    private saveOptionKey: string = "DocumentsDisplaySettings";
    private isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

    public render(): JSX.Element {
        const canApprove = sessionStore.canApproveDocument();

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.defineInitialColumnsForTable());

        const tableRenderOptions = this.tableRenderingOptions(columnsOptions);

        const documentsTableColumns = this.getTableColumns(columnsOptions);

        const markedForApproval = this.props.Model.get("DocumentsTableData").filter(d => d.IsSelected === true).map(d => d.Model.Model.Id).toArray();
        const approvedButton = markedForApproval.length && canApprove
            ? (<button className="btn btn-primary" onClick={() => this.onApproveClick(markedForApproval)} data-cy="documentSearch-approve">{cacheStore.GetString("ApproveLabel")}</button>)
            : (<button className="btn btn-primary disabled">{cacheStore.GetString("ApproveLabel")}</button>);

        if (this.props.Model.get("UpdatedTableColumn")) {
            const u = this.props.Model.get("UpdatedTableColumn");

            const idx = documentsTableColumns.findIndex(column => column.columnOptions.FieldName === u.columnOptions.FieldName);
            if (idx >= 0) {
                // deactivate active sort for all columns
                // start at one, don't mess with approve column
                for (let i = 1; i < documentsTableColumns.size - 1; i++) {
                    const column = documentsTableColumns.get(i);
                    if (column.columnOptions.FieldName !== u.columnOptions.FieldName) {
                        column.columnOptions.IsSortActive = false;
                        column.ResetHeader();
                    }
                }

                // set new active sort
                const activeColumn = documentsTableColumns.get(idx);
                activeColumn.columnOptions.IsSortActive = true;
                activeColumn.ResetHeader();
            }
        }

        let searchOptionsControl: JSX.Element = null;
        const hasPortalAccess = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

        if (this.props.Model.get("SearchOptionsOpen")) {
            searchOptionsControl = (
                createPortal(
                    <div
                        onClick={this.onContainerClick}
                        className={`filter-component filter-component-responsive ${(hasPortalAccess) ? "portal" : ""} ${styles.filterComponent}`}
                    >
                        <DocumentSettingsContainer Model={this.props.Model} />
                    </div>,
                    document.body
                )
            );
        }

        return this.renderNativeView(searchOptionsControl, approvedButton, tableRenderOptions, documentsTableColumns, columnsOptions);
    }

    private renderNativeView(
        searchOptionsControl: JSX.Element,
        approvedButton: JSX.Element,
        tableRenderOptions: ITableRenderOptions,
        documentsTableColumns: Immutable.List<MyleTableColumn>,
        columnsOptions: Immutable.List<MyleTableColumnOptions>
    ): JSX.Element {
        const columnsConfig = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const hasPortalButton = this.isPortalEnabled && !!columnsConfig.firstOrDefault(p => !p.IsHidden && p.ColumnUniqueName == "PortalStatus");

        return (
            <div className="workspace">
                {this.props.Model.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.Model.get("PortalLinkedItemModel")}
                        strategy={new SendDocumentStrategy()}
                    />
                )}
                {this.props.Model.get("AttachedCommunicationsPopupOpen") && (
                    <AttachedCommunicationsPopup
                        controlId="attached_communication_documentsearch"
                        topPosition={this.props.Model.get("AttachedCommunicationsPopupY")}
                        leftPosition={this.props.Model.get("AttachedCommunicationsPopupX")}
                        attachedCommunications={this.props.Model.get("AttachedCommunicationsPopupList")}
                        context={AttachedCommunicationType.Task}
                        handleRedirect={ac => this.taskRedirectClick(ac)}
                        handleClose={() => this.taskRedirectClose()}
                    />
                )}
                {this.state.showDocumentViewSettings &&
                    this.renderDocumentViewSettingPopup()
                }
                <CommonTableHeaderComponent
                    title={this.getDocumentsTitle(this.props.Model)}
                    searchPlaceholder={cacheStore.GetString("DocumentSearchLabel")}
                    searchText={this.props.Model.get("GlobalDocumentSearchText")}
                    additionalLeftComponent={[
                        <div
                            key="docSearchLeftRegistry"
                            className={documentSearchStyles.documentSearchLeftRegistryContainer}
                        >
                            <DocumentSearchLeftComponentRegistryView documentsModel={this.props.Model} />
                        </div>
                    ]}
                    leftSideFlexGrow
                    onSearch={text => this.FilterTextChanged(text)}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Documents.SearchSettings_OnRefreshClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Documents.SearchSettings_ShowOptionsButtonClick)}
                />
                <div id="posref" className="tb ws-scrollable documentSearch">
                    {searchOptionsControl}
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="doc_main_tb"
                            TableDataCy="document-table"
                            Columns={Immutable.List<MyleTableColumn>(documentsTableColumns)}
                            DataSource={this.props.Model.get("DocumentsTableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickFunction={(medicalDocumentTableItem, event) => this.onDocumentSelected(medicalDocumentTableItem, event)}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Myle_Documents_SearchResults_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_Documents_SearchResults_ColumnSortClick}
                            CssClass={hasPortalButton ? "has-portal-button" : ""}
                        />
                    </div>
                    <div className="clearfix" />
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("SelectedPageNumber")}
                                EventToDispatchOnPageChange={LocalOperations.Documents.Search_ChangePage}
                            />
                        </div>
                        <div className="col-lg-2 col-md-3 col-sm-4 text-right">
                            <div className={documentSearchStyles.actionButtonsContainer}>
                                <DocumentSearchActionButtonsRegistryView />
                                {approvedButton}
                            </div>
                        </div>
                    </div>
                </div>
                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnsConfig}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="docsColumnSettingsPopup"
                        CloseColumOrder={LocalOperations.Documents.DisplaySettings_CloseColumOrderPopupClick}
                    />
                )}
            </div>
        );
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MedicalDocumentTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "Approve":
                        return this.buildApproveColumn();

                    case "ReceiptDate":
                        return this.buildReceiptDateColumn();

                    case "UploadDate":
                        return this.buildUploadDateColumn();

                    case "Patients":
                        return this.buildPatientsColumn();

                    case "Type":
                        return this.buildTypeColumn();

                    case "Description":
                        return this.buildDescriptionColumn();

                    case "Doctor":
                        return this.buildDoctorColumn();

                    case "Status":
                        return this.buildStatusColumn();

                    case "PortalStatus":
                        return this.buildPortalStatusColumn();

                    case "Action":
                        return this.buildActionColumn();

                    case "Site":
                        return this.buildSiteColumn();
                }
            },
            false
        );
    }

    // Approve
    private buildApproveColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                if (item.Model.Model.CanBeApprovedByCurrentUser) {
                    return <div><input type="checkbox" id={item.Model.Model.Id} onChange={() => this.SetApproveSelectedDocuments(item)} checked={item.IsSelected} /></div>;
                }
                else {
                    return <div />;
                }
            },
            renderHeader: () => (
                <div className="checkbox-col"><input type="checkbox" className="header-input" onChange={() => this.SetApproveSelectedToAllDocuments()} checked={this.isSelectAllDocument} /></div>
            )
        };
    }

    // ReceiptDate
    private buildReceiptDateColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        const dateFormat = "yyyy/MM/d";
        return {
            renderCell: item => {
                const md = LuxonDateTimeParser.parse(item.Model.Model.ReceiptDate)?.toLocal();
                return (
                    <div
                        title={md?.toFormat(dateFormat)}
                        className={item.Model.Model.IsUrgent ? "red-row" : ""}
                    >
                        <span className={this.tableCellClass}>{md?.toFormat(dateFormat)} </span>
                    </div>
                );
            }
        };
    }

    // UploadDate
    private buildUploadDateColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let momentUploadDateList = item.Model.Model.DocumentPathList.map(x => {
                    if (x.CreationDate == null)
                        x.CreationDate = item.Model.Model.UploadDate;
                    return x.CreationDate;
                });
                let lastUploadDate = (momentUploadDateList && momentUploadDateList.length > 0) ? moment(momentUploadDateList.sort().reverse()[0]) : moment(item.Model.Model.UploadDate);

                return <div title={lastUploadDate.format("YYYY/MM/D")} className={item.Model.Model.IsUrgent ? "red-row" : ""}> <span className={this.tableCellClass}>{lastUploadDate.format("YYYY/MM/D")}</span> </div>;
            }
        };
    }

    // Patients
    private buildPatientsColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => (
                <div>
                    {item.Model.Model.Patients
                        .distinctByProperty(p => p.Id)
                        .map(patient => (
                            <div
                                key={patient.Id}
                                title={NameHelper.formatFullNameForHasName(patient)}
                                className={item.Model.Model.IsUrgent ? "red-row" : ""}
                            >
                                <HyperLinkRedirectControl
                                    target={RedirectTo.Patient}
                                    redirectValue={patient.Id}
                                    text={NameHelper.formatFullNameForHasName(patient)}
                                />
                            </div>
                        ))}
                </div>
            )
        };
    }

    // Type
    private buildTypeColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                const classId = cacheStore.GetLookupById(item.Model.Model.ClassId);
                const subclassId = cacheStore.GetLookupById(item.Model.Model.SubClassId);
                let lookupclass = new FlatLookupView();
                let lookupSubclass = new FlatLookupView();
                if (classId) { lookupclass = cacheStore.GetLookupById(item.Model.Model.ClassId); }
                if (subclassId) { lookupSubclass = cacheStore.GetLookupById(item.Model.Model.SubClassId); }

                const classModel = DocumentClassModel.FromLookup(lookupclass);
                const subClassModel = DocumentClassModel.FromLookup(lookupSubclass);

                return <div title={classModel.Name + " - " + subClassModel.Name} className={item.Model.Model.IsUrgent ? "red-row" : ""}> <span className={this.tableCellClass}> {classModel.Name + " - " + subClassModel.Name} </span> </div>;
            }
        };
    }

    // Description
    private buildDescriptionColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => (
                <div title={item.Model.Model.Subject} className={item.Model.Model.IsUrgent ? "red-row" : ""}> <span className={this.tableCellClass}>{item.Model.Model.Subject} </span></div>
            )
        };
    }

    // Doctor
    private buildDoctorColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                const providers = item.Model.Model
                    .ResponsibleProviders
                    .distinctByProperty(p => p.Id)
                    .map((provider, idx) => {
                        let prefix = "";
                        let prefixLookup = cacheStore.GetLookupById(provider.Prefix);
                        if (prefixLookup != null)
                            prefix = cacheStore.GetLocalizedValueByLookupId(prefixLookup.LookupID) + " ";

                        let providerElement = (
                            <div key={"doctor_" + idx} title={prefix + provider.LastName + ", " + provider.FirstName}>
                                {prefix + provider.LastName + ", " + provider.FirstName}
                            </div>
                        );
                        return providerElement;
                    });

                return <div className={item.Model.Model.IsUrgent ? "red-row" : ""}><span className={this.tableCellClass}>{providers}</span></div>;
            }
        };
    }

    // Status
    private buildStatusColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                const documentStatus = DocumentsHelper.GetDocumentStatusLabel(item.Model.Model);
                return <div title={documentStatus} className={item.Model.Model.IsUrgent ? "red-row" : ""}><span className={this.tableCellClass}>{documentStatus}</span></div>;
            }
        };
    }

    // Portal Status
    private buildPortalStatusColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                if (!PortalHelper.shouldShowPortalShareButton(item.Model.Model.Patients))
                    return <div />;

                const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(
                    item.Model
                );

                return (
                    <PortalStatusButton
                        icon="share"
                        onClick={e => this.PortalStatusClicked(item, e)}
                        {...buttonProps}
                    />
                );
            }
        };
    }

    // Site
    private buildSiteColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let DisplaySite = "";
                let site = cacheStore.GetSiteById(item.Model.Model.SiteId);
                if (site != null)
                    DisplaySite = site.Model.ClinicalName;

                return <div title={DisplaySite} className={item.Model.Model.IsUrgent ? "red-row" : ""}>  <span className={this.tableCellClass}>{DisplaySite} </span></div>;
            }
        };
    }

    private defineInitialColumnsForTable(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ApproveLabel"),
                FieldName: "Approve",
                WidthPixels: 38,
                IsFixedInPlace: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("AttachedOnLabel"),
                FieldName: "UploadDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.AttachedOnDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DocumentDateLabel"),
                FieldName: "ReceiptDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.DocumentDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientsLabel"),
                FieldName: "Patients",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ClassLabel"),
                FieldName: "Type",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.Type,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionLabel"),
                FieldName: "Description",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.Description,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 17.1
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DoctorLabel"),
                FieldName: "Doctor",
                RowClickEnabled: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "Status",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.Status,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 12
            }),
            this.isPortalEnabled &&
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusPortal"),
                FieldName: "PortalStatus",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: DocumentSortOrderType.PortalStatus,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Action",
                WidthPixels: 270,
                IsStaticWidth: true,
                IsFixedInPlace: true,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SiteDLabel"),
                FieldName: "Site",
                RowClickEnabled: true,
                IsSortable: true,
                IsHidden: true,
                SortColumn: DocumentSortOrderType.Site,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 12
            })
        ].filter(p => !!p);
    }

    private tableRenderingOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        return Immutable.fromJS({
            MinColWidth: 85,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(p => p.IsHidden === false).map(p => p.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private onApproveClick(markedForApproval: string[]): void {
        let operation = new LocalEvent(LocalOperations.Documents.Approve);
        operation.AddItem("DocumentIDs", markedForApproval);
        LocalEventDispatcher.dispatch(operation);
    }

    private FilterTextChanged(text: string): void {
        sessionStore.dispatch(
            LocalOperations.Documents.SearchSettings_SearchBoxTextChanged,
            { value: text }
        );
    }

    private SetApproveSelectedDocuments(item: MedicalDocumentTableItem): void {
        let operation = new LocalEvent(LocalOperations.Documents.Mark_Document_ForApproval);
        operation.AddItem("Document", item);
        LocalEventDispatcher.dispatch(operation);
    }

    private SetApproveSelectedToAllDocuments(): void {
        this.isSelectAllDocument = !this.isSelectAllDocument;
        let operation = new LocalEvent(LocalOperations.Documents.Mark_All_Document_ForApproval);
        operation.AddItem("SelectAllValue", this.isSelectAllDocument);
        LocalEventDispatcher.dispatch(operation);
    }

    private PortalStatusClicked(item: MedicalDocumentTableItem, e: React.MouseEvent): void {
        $(e.target).closest("tr").addClass("active");

        let operation = new LocalEvent(LocalOperations.Documents.PortalStatusClicked);
        operation.AddItem("doc", item.Model);
        LocalEventDispatcher.dispatch(operation);
    }

    private taskRedirectClick(attachedCommunication: AttachedCommunication): void {
        sessionStore.dispatch(
            LocalOperations.Documents.Search_AttachedCommunicationClick,
            {
                attachedCommunication: attachedCommunication
            }
        );
    }

    private taskRedirectClose(): void {
        sessionStore.dispatch(LocalOperations.Documents.Search_AttachedCommunicationClose);
    }
}

export { DocumentSearch };
